import { Box, Chip, Typography } from '@mui/material';
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  useRecordContext,
  type RaRecord,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { formatMoney } from './formatMoney';

/**
 * Cashbacks list — the member-facing transactions table (Phase 1, read-only).
 * Same proven pattern as Stores (#15/#17) and Users (#20): region-driven
 * <List>/<Datagrid>, RequireAccess gating, custom nav. List only — confirm /
 * cancel / reverse mutations come in Phase 2.
 *
 * A cashback row is a `transactions` record joined (on the DataProvider
 * interface, never the seed) to the resolved MEMBER (name + email) and STORE
 * (merchant name) by the provider's read-only `cashbacks` resource.
 *
 * Money columns are the MEMBER-FACING pair only: order value (grossAmount) and
 * cashback (cashbackAmount). `commissionAmount` (platform margin) is
 * deliberately omitted here — it is reserved for the future Earnings / FinOps
 * surface where margin is the point. Do not add it to this list.
 *
 * Date column is `occurredAt` — the conversion time (when the retailer reports
 * the sale happened), the ops-relevant date for support queries. `createdAt` is
 * the ingestion / row-creation time; `confirmedAt` / `expectedConfirmationAt`
 * carry the confirmation lifecycle (not shown in this list).
 *
 * Region behaviour is identical to the other lists: `activeRegion` flows through
 * the provider's single region->market mapping via the permanent `region`
 * filter. Flip the switcher and the rows change to that market.
 */

// The five backbone statuses, each a visually distinct chip so support can scan
// pending / confirmed / waiting / rejected / cancelled at a glance.
const STATUS_CHIP: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'info' | 'error' | 'default' }
> = {
  confirmed: { label: 'Confirmed', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  waiting: { label: 'Waiting', color: 'info' },
  rejected: { label: 'Rejected', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

export const StatusChip = () => {
  const record = useRecordContext();
  const status = (record?.status as string) ?? '';
  const chip = STATUS_CHIP[status] ?? { label: status, color: 'default' };
  return (
    <Chip
      size="small"
      label={chip.label}
      color={chip.color}
      variant={chip.color === 'default' ? 'outlined' : 'filled'}
    />
  );
};

// Member as a two-line cell: name (primary) + email (muted secondary). Cleaner
// and more scannable than two columns for an ops table.
export const MemberCell = () => {
  const record = useRecordContext();
  return (
    <Box>
      <Typography variant="body2">
        {(record?.memberName as string) ?? ''}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {(record?.memberEmail as string) ?? ''}
      </Typography>
    </Box>
  );
};

// Region is driven by the switcher, NOT a column filter. `q` is a
// case-insensitive substring search (matches member name/email + store name).
const cashbackFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'pending', name: 'Pending' },
      { id: 'confirmed', name: 'Confirmed' },
      { id: 'waiting', name: 'Waiting' },
      { id: 'rejected', name: 'Rejected' },
      { id: 'cancelled', name: 'Cancelled' },
    ]}
  />,
];

/** The list itself. Exported for testing without the access wrapper. */
export const CashbackListView = () => {
  const { activeRegion } = useRegion();
  return (
    <List
      resource="cashbacks"
      title="Cashbacks"
      filter={{ region: activeRegion }}
      filters={cashbackFilters}
      sort={{ field: 'occurredAt', order: 'DESC' }}
      perPage={25}
      exporter={false}
    >
      {/* Clicking a row opens the cashback detail (where confirm/reject lives).
          No bulk actions. */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <FunctionField
          label="Member"
          sortBy="memberName"
          render={() => <MemberCell />}
        />
        <FunctionField
          label="Store"
          sortBy="storeName"
          render={(record: RaRecord) => (record.storeName as string) ?? ''}
        />
        <FunctionField
          label="Order value"
          sortBy="grossAmount"
          render={(record: RaRecord) =>
            formatMoney(record.grossAmount, record.currency)
          }
        />
        <FunctionField
          label="Cashback"
          sortBy="cashbackAmount"
          render={(record: RaRecord) =>
            formatMoney(record.cashbackAmount, record.currency)
          }
        />
        <FunctionField
          label="Status"
          sortBy="status"
          render={() => <StatusChip />}
        />
        <DateField source="occurredAt" label="Date" showTime />
      </Datagrid>
    </List>
  );
};

/**
 * Gated entry point registered as the `cashbacks` Resource's list. Read-only,
 * so `viewer` (read everywhere) sees it alongside `super_admin`; editor /
 * support / fraud lack `cashbacks:read` and never see the route or menu item.
 */
export const CashbackList = () => (
  <RequireAccess capability="cashbacks:read">
    <CashbackListView />
  </RequireAccess>
);
